import { StandardEvents } from '@shopify/events';

/**
 * Subscribe & save / one-time purchase picker (blocks/mika-subscription.liquid).
 *
 * - Enables the selling_plan input of the buy buttons form for subscriptions, disables it for one-time.
 * - Updates the subscription price, per-unit price and savings for the chosen delivery frequency.
 * - Shows the chosen price in Mika style price blocks and the add to cart button of the same section.
 * - Re-renders itself on variant change, keeping the chosen mode and frequency.
 */

/** One-time markup of each price container, restored when one-time is chosen again */
const oneTimePrices = new WeakMap();

class MikaSubscription extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.addEventListener('change', this.#handleChange);
    this.section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#apply();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @returns {'subscription' | 'onetime'} */
  get mode() {
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-mode-input]:checked'));
    return checked?.value === 'subscription' ? 'subscription' : 'onetime';
  }

  /** @returns {HTMLOptionElement | null} */
  get planOption() {
    const select = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-plan-select]'));
    return select?.selectedOptions[0] ?? null;
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (target.matches('[data-mode-input], [data-plan-select]')) this.#apply();
  };

  /** @param {any} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;
        if (!detail.newProduct && detail.productId && detail.productId !== this.dataset.productId) return;

        const fresh = detail.html.querySelector(`mika-subscription[data-block-id="${this.dataset.blockId}"]`);

        // Run after the price and buy buttons have swapped in their new markup
        setTimeout(() => {
          this.#clearPriceCache();
          if (fresh) this.#refresh(fresh);
          this.#apply();
        });
      })
      .catch(() => {});
  };

  /**
   * Swaps in the markup rendered for the new variant, keeping the shopper's choices.
   * @param {Element} fresh
   */
  #refresh(fresh) {
    const mode = this.mode;
    const planId = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-plan-select]'))?.value;

    this.innerHTML = fresh.innerHTML;
    for (const name of [
      'data-product-id',
      'data-onetime-price',
      'data-onetime-unit-price',
      'data-onetime-cents',
      'data-compare-cents',
      'data-compare-price',
      'data-currency',
    ]) {
      const value = fresh.getAttribute(name);
      if (value === null) this.removeAttribute(name);
      else this.setAttribute(name, value);
    }
    // Variants without a plan hide the block
    this.hidden = fresh.hasAttribute('hidden');

    const modeInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`[data-mode-input][value="${mode}"]`)
    );
    if (modeInput) modeInput.checked = true;

    const select = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-plan-select]'));
    if (select && planId && Array.from(select.options).some((option) => option.value === planId)) {
      select.value = planId;
    }
  }

  #apply() {
    // A hidden block (variant without a plan) always buys one-time
    const mode = this.hidden ? 'onetime' : this.mode;
    const option = this.planOption;
    this.dataset.mode = mode;

    const planInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-selling-plan-input]'));
    if (planInput) {
      if (option) planInput.value = option.value;
      planInput.disabled = mode !== 'subscription';
    }

    if (option) {
      const { price = '', unitPrice = '', percent = '' } = option.dataset;
      this.querySelectorAll('[data-sub-price]').forEach((el) => (el.textContent = price));
      this.querySelectorAll('[data-sub-unit-price]').forEach((el) => (el.textContent = unitPrice));
      this.querySelectorAll('[data-sub-percent]').forEach((el) => (el.textContent = percent));
    }

    // Editor preview with sample prices: leave the rest of the page alone, nothing is submitted
    if (this.hasAttribute('data-preview')) {
      if (planInput) planInput.disabled = true;
      return;
    }

    const subscription =
      mode === 'subscription' && option
        ? { price: option.dataset.price ?? '', unitPrice: option.dataset.unitPrice ?? '' }
        : null;

    this.#syncPriceBlocks(subscription);
    this.#syncSaveBadges(Boolean(subscription));
    this.#syncAddToCart(subscription);
    this.#syncVariantButtons(Boolean(subscription));
  }

  /**
   * "Save $X.XX" next to Mika price blocks: compare-at (or the one-time price)
   * minus what the shopper pays in the chosen mode. Hidden when nothing is saved.
   * @param {boolean} subscribed
   */
  #syncSaveBadges(subscribed) {
    const scope = this.section ?? document;

    scope.querySelectorAll('product-price.price-block--mika').forEach((block) => {
      if (!(block instanceof HTMLElement)) return;
      const badge = block.querySelector('[data-price-save]');
      if (!badge) return;

      const onetime = Number(this.dataset.onetimeCents);
      const paid = subscribed ? Number(this.planOption?.dataset.priceCents) : onetime;
      // Older markup without the cents attributes: leave the server-rendered badge alone
      if (!Number.isFinite(onetime) || !Number.isFinite(paid)) return;

      const compare = Number(this.dataset.compareCents) || onetime;
      const save = compare - paid;
      if (save <= 0) {
        badge.hidden = true;
        return;
      }

      const currency = this.dataset.currency || 'USD';
      const locale = document.documentElement.lang || 'en-US';
      const formatted = new Intl.NumberFormat(locale, { style: 'currency', currency }).format(save / 100);
      badge.textContent = `Save ${formatted}`;
      badge.hidden = false;
    });
  }

  /**
   * A variant change swaps fresh one-time markup into the price containers;
   * drop the cached copies so a later one-time toggle doesn't restore the
   * previous variant's price.
   */
  #clearPriceCache() {
    const scope = this.section ?? document;
    scope
      .querySelectorAll('product-price.price-block--mika [ref="priceContainer"]')
      .forEach((container) => oneTimePrices.delete(container));
  }

  /**
   * Per-unit prices on the variant picker buttons follow the purchase mode.
   * @param {boolean} subscribed
   */
  #syncVariantButtons(subscribed) {
    const scope = this.section ?? document;

    scope.querySelectorAll('.variant-option__button-label__sub[data-onetime-unit]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const text = subscribed && el.dataset.subUnit ? el.dataset.subUnit : el.dataset.onetimeUnit;
      if (text !== undefined && el.textContent !== text) el.textContent = text;
    });
  }

  /**
   * Shows the subscription price (with the one-time price struck through) in Mika price blocks.
   * @param {{ price: string, unitPrice: string } | null} subscription
   */
  #syncPriceBlocks(subscription) {
    const scope = this.section ?? document;

    scope.querySelectorAll('product-price.price-block--mika').forEach((block) => {
      if (!(block instanceof HTMLElement)) return;
      block.dataset.purchaseMode = subscription ? 'subscription' : 'onetime';

      const container = block.querySelector('[ref="priceContainer"]');
      if (!container) return;
      if (!oneTimePrices.has(container)) oneTimePrices.set(container, container.innerHTML);

      if (!subscription) {
        container.innerHTML = oneTimePrices.get(container);
        return;
      }

      const showUnit = Boolean(container.querySelector('.price-block__per-unit')) && block.dataset.unitLabel;
      container.replaceChildren(
        priceGroup('price-item__group price', subscription.price),
        priceGroup(
          'price-item__group price-item--regular compare-at-price',
          this.dataset.comparePrice ?? this.dataset.onetimePrice ?? ''
        )
      );

      if (showUnit) {
        const unit = document.createElement('span');
        unit.className = 'price-block__per-unit';
        unit.textContent = `${subscription.unitPrice} ${block.dataset.unitLabel}`;
        container.append(unit);
      }
    });
  }

  /**
   * Updates the price shown in the add to cart button.
   * @param {{ price: string } | null} subscription
   */
  #syncAddToCart(subscription) {
    const scope = this.section ?? document;

    scope.querySelectorAll('.add-to-cart__price').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      // A variant change re-renders the button, which clears this attribute
      if (el.dataset.onetimeText === undefined) el.dataset.onetimeText = el.textContent ?? '';

      el.textContent = subscription
        ? `${el.dataset.separator ?? ''} ${subscription.price}`.trim()
        : el.dataset.onetimeText;
    });
  }
}

/**
 * @param {string} className
 * @param {string} text
 */
function priceGroup(className, text) {
  const group = document.createElement('span');
  group.className = className;
  const bdi = document.createElement('bdi');
  bdi.textContent = text;
  group.append(bdi);
  return group;
}

if (!customElements.get('mika-subscription')) {
  customElements.define('mika-subscription', MikaSubscription);
}
